// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// Devextream imports
import { DxDataGridComponent, DxFileUploaderComponent, DxFormComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Constant imports
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { ActionConstants } from '../../../../_constants/action-constants';

// Service imports
import { FileReaderService } from '../../../../_services/shared-services/file-reader.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { VendorContractService } from '../../_services/vendor-contracts.service';
import { CiraHelpService } from '../../../../_services/shared-services/cira-help.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { VendorManagementSharedService } from '../../_services/vendor-management-shared.service';

// Model imports
import { VendorContractBaseParameterDto } from '../../_models/vendor-contract-base-parameter-dto';
import { VendorContractDocumentRawDto } from '../../../../shared-ciramail-module/_models/vendor-contract-document-raw-dto';
import { VendorContractEditorResultDto } from '../../_models/vendor-contract-editor-result-dto';
import { VendorContractRawDto } from '../../_models/vendor-contract-raw-dto';
import { DownloadVendorContractDocumentParameterDto } from '../../_models/download-vendor-contract-document-parameter-dto';

@Component({
    selector: 'app-contract-editor',
    templateUrl: './contract-editor.component.html',
    styleUrls: ['./contract-editor.component.scss']
})
export class ContractEditorComponent implements OnInit {
    @ViewChild('contractEditorForm', { static: false }) contractEditorForm: DxFormComponent;
    @ViewChild('contractEditorPopup', { static: false }) contractEditorPopup: DxPopupComponent;
    @ViewChild('fileUploader', { static: false }) fileUploader: DxFileUploaderComponent;
    @ViewChild('contractDocumentGrid', { static: false }) contractDocumentGrid: DxDataGridComponent;

    @Input() vendorContractId: number;
    @Input() vendorName: string;
    @Input() communityId: number;
    @Input() communityVendorId: number;
    @Input() vendorCode: string;

    @Output() onClose = new EventEmitter<boolean>();

    visible = false;
    popupTitle: string;
    helpLink: string;
    contractDocuments: VendorContractDocumentRawDto[] = [];

    vendorContract: VendorContractRawDto = new VendorContractRawDto();
    contractsHelpKey: string;

    contractEditorData: VendorContractEditorResultDto;
    newContractDocument: any[] = [];
    showContractDocument = false;
    documentTitle = 'Attach Contract Document';
    uploadedFile: File[] = [];

    iconAttach = IconConstants.ATTACHMENT;
    iconUpload = IconConstants.UPLOAD;
    iconSave = IconConstants.SAVE;
    iconClose = IconConstants.CLOSE;
    iconDelete = IconConstants.DELETE;

    loadingVisible = false;
    isContractTypeOneTime = false;
    isContractTypeRecurringAndContractTermPerpetual = false;

    constructor(
        private readonly fileReaderService: FileReaderService,
        private readonly errorService: ServerErrorService,
        private readonly vendorContractService: VendorContractService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly helperService: HelperService,
        private readonly vendorManagementSharedService: VendorManagementSharedService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(this.contractsHelpKey);
    }

    ngOnInit() {
        this.popupTitle = Utils.notNullAndDefined(this.vendorContractId)
            ? `Edit Contract For ${this.vendorName}`
            : `New Contract For ${this.vendorName}`;
        this.newContractDocument = [
            { value: 1, name: 'Attach Document', icon: this.iconAttach },
            { value: 2, name: 'Upload Document', icon: this.iconUpload }
        ];
        this.populateContractEditor();
    }

    onShown(): void {
        DxWidgetUtils.instantRepaint(this.contractEditorPopup);
    }

    onHidden(refresh = false): void {
        this.visible = false;
        this.onClose.emit(refresh);
    }

    onGridContentReady(event: any): void {
        event.component.filter([
            ['crudAction', '<>', ActionConstants.DELETE],
            'and',
            ['crudAction', '<>', ActionConstants.LOCAL_DELETE]
        ]);
    }

    onNewContractDocumentClick(event: any): void {
        const item = event.itemData;
        if (Utils.equalsIgnoreCase(item.name, 'Upload Document')) {
            this.onBrowseClick();
        } else {
            this.showContractDocument = true;
        }
    }

    onDocumentSelect(event: any): void {
        if (Utils.notNullAndDefined(event)) {
            const document: VendorContractDocumentRawDto = new VendorContractDocumentRawDto();
            document.fileId = event.fileId;
            document.fileName = event.fileName;
            document.crudAction = ActionConstants.ADD;
            this.contractDocuments.push(document);
        }
    }

    onBrowseClick(): void {
        const uploader: any = this.fileUploader.instance;
        uploader._isCustomClickEvent = true;
        uploader._$fileInput[0].click();
    }

    onAttachmentsSelect(event: any): void {
        const localAttachments = event.value;
        const contractDocuments: Array<VendorContractDocumentRawDto> = [];
        if (Utils.notNullOrEmpty(this.contractDocuments)) {
            localAttachments.forEach((attachment) => {
                const existingAttachment = this.contractDocuments.find((file) =>
                    Utils.equalsIgnoreCase(file.fileName, attachment.name)
                );

                if (Utils.isNullOrUndefined(existingAttachment)) {
                    contractDocuments.push(attachment);
                }
            });

            if (Utils.notNullOrEmpty(contractDocuments)) {
                this.uploadDocument(contractDocuments);
            }
        } else {
            this.uploadDocument(localAttachments);
        }
    }

    uploadDocument(localAttachments: any[]): void {
        localAttachments.forEach((attachment) => {
            this.fileReaderService.readFileToBase64(attachment).subscribe((base64Data) => {
                const document: VendorContractDocumentRawDto = new VendorContractDocumentRawDto();
                document.fileBase64String = base64Data;
                document.fileName = attachment.name;
                document.crudAction = ActionConstants.ADD;
                this.contractDocuments.push(document);
            });
        });
    }

    onDocumentClick(data: VendorContractDocumentRawDto): void {
        const file = data;
        if (Utils.notNullAndDefined(file.fileId)) {
            const param: DownloadVendorContractDocumentParameterDto = new DownloadVendorContractDocumentParameterDto();
            param.communityId = this.communityId;
            param.communityVendorId = this.communityVendorId;
            param.vendorCode = this.vendorCode;
            param.fileId = file.fileId;
            param.vendorContractId = this.vendorContractId;
            param.clearCache = true;

            this.loadingVisible = true;
            this.vendorContractService.downloadContractDocument(param).subscribe(
                (result: Blob) => {
                    const fileData = result;
                    this.helperService.showBlobInBrowserTab(fileData, file.fileName);
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        } else if (file instanceof File) {
            const blob: Blob = new Blob([file]);
            this.helperService.showBlobInBrowserTab(blob, file.name);
        }
    }

    onDeleteClick(row: VendorContractDocumentRawDto): void {
        if (Utils.notNullAndDefined(row.fileId)) {
            row.crudAction = ActionConstants.DELETE;
        } else {
            row.crudAction = ActionConstants.LOCAL_DELETE;
        }
        setTimeout(() => {
            DxWidgetUtils.instantRefresh(this.contractDocumentGrid);
        }, 100);
    }

    onVendorContractChanged(): void {
        this.processContractEditor();
    }

    onVendorContractTermChanged(): void {
        this.processContractEditor();
    }

    onEndDateChanged(): void {
        if (Number(this.vendorContract.endDate) <= Number(this.vendorContract.effectiveDate)) {
            this.errorService.updateSubjectErrorMessage('End date should be greater than Effective date.');
            this.vendorContract.endDate = null;
        }
    }

    onSaveClick(): void {
        if (DxWidgetUtils.hasInstance(this.contractEditorForm)) {
            const validator = this.contractEditorForm.instance.validate();
            if (Utils.isTrue(validator.isValid)) {
                this.vendorContract.documents = this.contractDocuments.filter(
                    (file) =>
                        Utils.notNullOrEmpty(file.crudAction) &&
                        Utils.notEqualsIgnoreCase(file.crudAction, ActionConstants.LOCAL_DELETE)
                );
                this.vendorContract.clearCache = true;
                this.vendorContract.communityId = this.communityId;
                if (Utils.notNullAndDefined(this.communityVendorId)) {
                    this.vendorContract.communityVendorId = this.communityVendorId;
                }

                if (Utils.notNullAndDefined(this.vendorCode)) {
                    this.vendorContract.vendorCode = this.vendorCode;
                }

                let service: any;
                if (Utils.notNullAndDefined(this.vendorContractId)) {
                    this.vendorContract.contractId = this.vendorContractId;
                    service = this.vendorManagementSharedService.updateContract(this.vendorContract);
                } else {
                    service = this.vendorManagementSharedService.addContract(this.vendorContract);
                }

                this.loadingVisible = true;
                service.subscribe(
                    () => {
                        const message = Utils.notNullAndDefined(this.vendorContractId)
                            ? 'Contract updated successfully'
                            : 'Contract added successfully';
                        notify(message, 'success', MessageInterval.defaultErrorMsgInterval);
                        this.loadingVisible = false;
                        this.onHidden(true);
                    },
                    (error) => {
                        this.errorService.displayError(error);
                        this.loadingVisible = false;
                    }
                );
            }
        }
    }

    private populateContractEditor(): void {
        const param: VendorContractBaseParameterDto = new VendorContractBaseParameterDto();
        param.communityId = this.communityId;
        param.communityVendorId = this.communityVendorId;
        param.vendorCode = this.vendorCode;
        param.vendorContractId = this.vendorContractId;
        param.clearCache = true;
        this.loadingVisible = true;
        this.vendorManagementSharedService.getContractEditor(param).subscribe(
            (result: VendorContractEditorResultDto) => {
                this.contractEditorData = result;
                this.vendorContract = this.contractEditorData.vendorContract;
                this.contractsHelpKey = this.contractEditorData.contractsHelpKey;
                this.contractDocuments = this.vendorContract.documents;
                this.setDefaultValues();
                this.processContractEditor();
                this.visible = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private setDefaultValues(): void {
        if (Utils.isNullOrUndefined(this.vendorContract.contractTypeCode)) {
            const selectedContractType = this.contractEditorData.contractTypes.find((d) => Utils.isTrue(d.isDefault));
            this.vendorContract.contractTypeCode = Utils.notNullAndDefined(selectedContractType)
                ? selectedContractType.key
                : null;
        }

        if (Utils.isNullOrUndefined(this.vendorContract.contractTermCode)) {
            const selectedContractTerm = this.contractEditorData.contractTerms.find((d) => Utils.isTrue(d.isDefault));
            this.vendorContract.contractTermCode = Utils.notNullAndDefined(selectedContractTerm)
                ? selectedContractTerm.key
                : null;
        }
    }

    private processContractEditor(): void {
        if (Utils.equalsIgnoreCase(this.vendorContract.contractTypeCode, 'One Time')) {
            this.isContractTypeOneTime = true;
            this.vendorContract.contractTermCode = 'Fixed';
            this.vendorContract.autoRenewalFrequencyCode = null;
            this.vendorContract.autoRenewalPeriod = null;
        } else {
            this.isContractTypeOneTime = false;
        }

        if (
            Utils.equalsIgnoreCase(this.vendorContract.contractTypeCode, 'Recurring') &&
            Utils.equalsIgnoreCase(this.vendorContract.contractTermCode, 'Perpetual')
        ) {
            this.isContractTypeRecurringAndContractTermPerpetual = true;
            this.vendorContract.autoRenewalFrequencyCode = null;
            this.vendorContract.endDate = null;
        } else {
            this.isContractTypeRecurringAndContractTermPerpetual = false;
        }
    }
}
